/**
 * `ProjectStoreProvider` + `useProjectStore` — the React-side wiring
 * that lets components reach the active `ProjectStore` (local or
 * cloud) without each one importing the right module.
 *
 * The free-mode app wraps with `localStorageStore`. The cloud app
 * builds an `ApiStore` once auth resolves and wraps with that.
 * Components further down call `useProjectStore()` and don't care
 * which backend they're talking to.
 */

import { createContext, type ReactNode, useContext } from 'react';

import type { ProjectStore } from '../stores/types.js';

const StoreContext = createContext<ProjectStore | null>(null);

export function ProjectStoreProvider({
  store,
  children,
}: {
  store: ProjectStore;
  children: ReactNode;
}): JSX.Element {
  return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>;
}

export function useProjectStore(): ProjectStore {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useProjectStore must be used inside <ProjectStoreProvider>');
  return ctx;
}
